import type { ApolloClient } from '@apollo/client';
import { bulkAssignGroupRolePermissionsMutation } from 'graphql-queries';

export type WizardStep = 'teams' | 'permissions' | 'review';

export type SubmitOutcome = {
  succeededIds: string[];
  failed: Array<{ groupRoleId: string; reason: string }>;
};

type Snapshot = {
  step: WizardStep;
  selectedGroupRoleIds: string[];
  selectedPermissions: string[];
  isSubmitting: boolean;
  submitError: string | null;
  submitOutcome: SubmitOutcome | null;
  canAdvance: boolean;
};

const STEP_ORDER: WizardStep[] = ['teams', 'permissions', 'review'];

/**
 * Owns all state, navigation, validation, and the mutation call for the
 * bulk team permissions-assignment wizard. A plain TS class (no React or
 * Apollo-hook imports) so it is trivially unit-testable with a fake
 * `{ mutate: jest.fn() }` client.
 */
export class PermissionsAssignmentWizardEngine {
  private step: WizardStep = 'teams';
  private selectedGroupRoleIds: string[] = [];
  private selectedPermissions: string[] = [];
  private isSubmitting = false;
  private submitError: string | null = null;
  private submitOutcome: SubmitOutcome | null = null;
  private readonly listeners = new Set<() => void>();
  private snapshot: Snapshot = this.buildSnapshot();

  constructor(
    private readonly client: Pick<ApolloClient<object>, 'mutate'>,
    private readonly channelIds: string[]
  ) {}

  /**
   * Returns a cached snapshot reference that only changes when state actually
   * mutates (rebuilt in `notify()`). `useSyncExternalStore` relies on
   * `Object.is` equality between successive calls to know the store hasn't
   * changed — returning a fresh object literal here every call would make
   * React think the store changes on every render and loop forever.
   */
  getSnapshot(): Snapshot {
    return this.snapshot;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);

    return () => this.listeners.delete(listener);
  }

  selectGroupRoleIds(ids: string[]): void {
    this.selectedGroupRoleIds = ids;
    this.notify();
  }

  selectPermissions(permissions: string[]): void {
    this.selectedPermissions = permissions;
    this.notify();
  }

  next(): void {
    if (!this.canAdvance()) return;

    const currentIndex = STEP_ORDER.indexOf(this.step);

    if (currentIndex < STEP_ORDER.length - 1) {
      this.step = STEP_ORDER[currentIndex + 1];
      this.notify();
    }
  }

  back(): void {
    const currentIndex = STEP_ORDER.indexOf(this.step);

    if (currentIndex > 0) {
      this.step = STEP_ORDER[currentIndex - 1];
      this.notify();
    }
  }

  async confirm(): Promise<void> {
    this.isSubmitting = true;
    this.submitError = null;
    this.notify();

    try {
      const result = await this.client.mutate({
        mutation: bulkAssignGroupRolePermissionsMutation,
        variables: {
          input: {
            groupRoleIds: this.selectedGroupRoleIds,
            permissions: this.selectedPermissions,
          },
        },
      });

      this.submitOutcome = result.data.bulkAssignGroupRolePermissions;
    } catch (error) {
      this.submitError = error instanceof Error ? error.message : 'Unknown error';
    } finally {
      this.isSubmitting = false;
      this.notify();
    }
  }

  reset(): void {
    this.step = 'teams';
    this.selectedGroupRoleIds = [];
    this.selectedPermissions = [];
    this.isSubmitting = false;
    this.submitError = null;
    this.submitOutcome = null;
    this.notify();
  }

  getChannelIds(): string[] {
    return this.channelIds;
  }

  private canAdvance(): boolean {
    if (this.isSubmitting) return false;

    if (this.step === 'teams') return this.selectedGroupRoleIds.length > 0;

    if (this.step === 'permissions') return this.selectedPermissions.length > 0;

    return true;
  }

  private buildSnapshot(): Snapshot {
    return {
      step: this.step,
      selectedGroupRoleIds: this.selectedGroupRoleIds,
      selectedPermissions: this.selectedPermissions,
      isSubmitting: this.isSubmitting,
      submitError: this.submitError,
      submitOutcome: this.submitOutcome,
      canAdvance: this.canAdvance(),
    };
  }

  private notify(): void {
    this.snapshot = this.buildSnapshot();
    this.listeners.forEach(listener => listener());
  }
}
